import { defineConfig } from '@magicdoorinc/date';
import { useLocalization, createLazyResource, createMutation } from '@magicdoorinc/ui';
import { createEffect, createMemo } from 'solid-js';
import { rememberCompanyName, rememberCompanyBusinessMode } from '~/config/auth';
import { createMagicDoorContext } from '~/contexts/utils';
import { PaymentMode, setMode } from '~/hooks/usePaymentMode';
import { CompaniesRepository } from '~/repositories/companiesRepository';
import { MagicDoorFeature } from '~/swagger/portal/Api';
import { windowsToIana } from '~/utils/date';
import type {
  CompanyDomainDto,
  CompanySubUrlDto,
  RequiredAddressDto,
  UpdateCompanyDto,
  UpdatePortalSettingsDto,
} from '~/swagger/portal/Api';

const repo = new CompaniesRepository();

export const [CompanieProvider, useCurrentCompany] = createMagicDoorContext('Companies', () => {
  const [currentCompany, { refetch: refetchCompanys, mutate: mutateCompanies }] = createLazyResource(() => repo.getCompanies());
  const [portalSettings, { mutate: psMutate }] = createLazyResource(() => repo.getPortalSettings());

  const checkFeature = (feature: MagicDoorFeature) => {
    const features = currentCompany()?.features;
    if (!features) {
      return false;
    }
    return features.includes(feature);
  };

  const updateSubUrl = createMutation(async (model: CompanySubUrlDto | CompanyDomainDto) => {
    const updated = await repo.updateSubUrl(model);
    return updated;
  });

  const updateCustomUrl = createMutation(async (model: CompanyDomainDto | CompanySubUrlDto) => {
    const updated = await repo.updateCustomUrl(model);
    return updated;
  });

  const validateSubUrl = createMutation(async (model: CompanySubUrlDto) => {
    return await repo.validateSubUrl(model);
  });

  const validateCustomUrl = createMutation(async (model: CompanyDomainDto) => {
    return await repo.validateCustomUrl(model);
  });

  const updateCompanyProfile = createMutation(async (model: Partial<UpdateCompanyDto>) => {
    const _currentCompany = currentCompany();
    if (!_currentCompany) {
      throw new Error('Company not loaded');
    }
    const updated = await repo.updateCompanyProfile({
      name: _currentCompany.name,
      email: _currentCompany.email,
      language: _currentCompany.language as UpdateCompanyDto['language'],
      address: _currentCompany.address as RequiredAddressDto,
      ...model,
    });
    refetchCompanys();
    return updated;
  });

  const uploadPortalSettingsFile = createMutation(async (file: File) => {
    return await repo.uploadPortalSettingsFile(file);
  });

  const updatePortalSettings = createMutation(async (model: UpdatePortalSettingsDto) => {
    const updated = await repo.updatePortalSettings(model);
    psMutate(updated);
  });

  const getWebsiteUrl = (pathName: string) => {
    const _publicUrl = currentCompany()?.publicUrl;

    if (!_publicUrl) {
      return '';
    }

    const base = _publicUrl.replace(/\/$/, '');
    const path = pathName.startsWith('/') ? pathName : `/${pathName}`;
    return `${base}${path}`;
  };

  const isWewelo = createMemo(() => {
    return currentCompany()?.id === '1192372134000000000';
  });

  const { currentLanguage } = useLocalization();

  createEffect(() => {
    const _company = currentCompany();
    if (_company?.id && _company?.name) {
      rememberCompanyName(_company.id, _company.name);
    }
    if (_company?.id && _company?.businessMode) {
      rememberCompanyBusinessMode(_company.id, _company.businessMode);
    }
  });

  createEffect(() => {
    const _companies = currentCompany();
    const lang = currentLanguage();

    if (_companies) {
      defineConfig({
        tz: windowsToIana(_companies?.timeZoneInfo ?? 'UTC'),
        localeGetter: () => lang,
      });
    }
  });

  createEffect(() => {
    const isPayMode = checkFeature(MagicDoorFeature.CentralizedPayments);
    setMode(isPayMode ? PaymentMode.Pay : PaymentMode.Portal);
  });

  return {
    currentCompany,
    refetchCompanys,
    updateSubUrl,
    updateCustomUrl,
    validateSubUrl,
    validateCustomUrl,
    updateCompanyProfile,
    portalSettings,
    uploadPortalSettingsFile,
    updatePortalSettings,
    getWebsiteUrl,
    isWewelo,
    mutateCompanies,
    checkFeature,
  };
});
